import { useCallback, useEffect, useState } from 'react'
import { SectionCard, DropZone, DataTable, Checkbox, BORDER, colors, type Column } from '@datahub/design-system'
import { DOCUMENT_TYPES, UPLOAD_ACCEPT, type PatchOperation } from '@datahub/shared'
import { get, post, patch, del } from '../lib/api'
import { StatusTag } from '../components/StatusTag'
import type { AttachmentView } from '../lib/types'
import { uploadFiles, removeAttachment, type UploadResult } from './upload-files'

interface DocumentsTabProps {
  toggled: boolean
  onToggle: (value: boolean) => void
  push: (...ops: PatchOperation[]) => void
  readOnly?: boolean
  reloadSignal?: number
}

const contentType = (file: File) => file.type || 'application/octet-stream'

/** Tab 8. Uploads go straight to object storage through a presigned URL (E7.1). */
export function DocumentsTab({ toggled, onToggle, readOnly, reloadSignal }: DocumentsTabProps) {
  const [items, setItems] = useState<AttachmentView[]>([])
  const [busy, setBusy] = useState(false)
  const [results, setResults] = useState<UploadResult[]>([])
  const [error, setError] = useState<string | null>(null)
  /** Types that are being saved, by attachment id. The select shows the new value and waits. */
  const [savingType, setSavingType] = useState<Record<string, string>>({})
  /** The message of a remove that the API refused, by attachment id. It shows in the row of the file. */
  const [removeErrors, setRemoveErrors] = useState<Record<string, string>>({})

  const reload = useCallback(async () => {
    const res = await get<{ attachments: AttachmentView[] }>('/portal/uploads')
    setItems(res.attachments)
  }, [])

  useEffect(() => { void reload() }, [reload, reloadSignal])

  const upload = async (files: File[]) => {
    setBusy(true)
    setError(null)
    setResults([])
    try {
      await uploadFiles(
        files,
        {
          presign: (file) => post<{ attachmentId: string; uploadUrl: string }>('/portal/uploads/presign', {
            fileName: file.name, mimeType: contentType(file), sizeBytes: file.size,
          }),
          put: async (url, file) => (await fetch(url, { method: 'PUT', body: file, headers: { 'content-type': contentType(file) } })).ok,
          exists: async (id) => (await get<{ exists: boolean }>(`/portal/uploads/${id}/exists`)).exists,
          discard: async (id) => { await del(`/portal/uploads/${id}`) },
        },
        (result) => setResults((prev) => [...prev, result]),
      )
    } finally {
      await reload().catch(() => setError('We could not load the attached files. Reload the page.'))
      setBusy(false)
    }
  }

  const setDocumentType = async (attachment: AttachmentView, value: string) => {
    setError(null)
    setSavingType((prev) => ({ ...prev, [attachment.id]: value }))
    try {
      await patch(`/portal/uploads/${attachment.id}`, { documentType: value === '' ? null : value })
    } catch (err) {
      setError(err instanceof Error ? err.message : `We could not save the type of ${attachment.fileName}`)
    }
    await reload().catch(() => setError('We could not load the attached files. Reload the page.'))
    setSavingType(({ [attachment.id]: _saved, ...rest }) => rest)
  }

  const remove = async (attachment: AttachmentView) => {
    setRemoveErrors(({ [attachment.id]: _old, ...rest }) => rest)
    const message = await removeAttachment(attachment.id, async (id) => { await del(`/portal/uploads/${id}`) })
    if (message) setRemoveErrors((prev) => ({ ...prev, [attachment.id]: message }))
    await reload().catch(() => setError('We could not load the attached files. Reload the page.'))
  }

  const columns: Column<AttachmentView>[] = [
    { key: 'name', header: 'File', width: 320, sticky: true, render: (a) => <bdi>{a.fileName}</bdi> },
    {
      key: 'type', header: 'Document type', width: 220,
      render: (a) => {
        // A type from before this list stays visible until the contact picks a new one.
        const legacy = a.documentType && !(DOCUMENT_TYPES as readonly string[]).includes(a.documentType) ? [a.documentType] : []
        return (
          <select
            aria-label={`Document type of ${a.fileName}`}
            value={savingType[a.id] ?? a.documentType ?? ''}
            disabled={readOnly || a.submitted || a.id in savingType}
            aria-busy={a.id in savingType}
            onChange={(e) => void setDocumentType(a, e.target.value)}
            className="bg-background-body text-white font-jakarta outline-none"
            style={{ border: BORDER, fontSize: 12, padding: '2px 6px', height: 24, width: '100%' }}
          >
            <option value="">Choose a type</option>
            {[...legacy, ...DOCUMENT_TYPES].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )
      },
    },
    { key: 'size', header: 'Size', width: 110, align: 'right', render: (a) => `${Math.max(1, Math.round(a.sizeBytes / 1024))} KB` },
    { key: 'scan', header: 'Scan', width: 130, render: (a) => <StatusTag status={a.virusScanStatus} /> },
    {
      key: 'actions', header: '', width: 160,
      render: (a) => (
        <div className="flex flex-col" style={{ gap: 2 }}>
          <button
            type="button"
            disabled={readOnly || a.submitted}
            onClick={() => void remove(a)}
            className="text-text-support hover:text-white self-start"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
          >
            Remove
          </button>
          {removeErrors[a.id] && (
            <span role="alert" className="font-mulish" style={{ fontSize: 10, lineHeight: '12px', color: colors.buttonDestructive, whiteSpace: 'normal' }}>
              {removeErrors[a.id]}
            </span>
          )}
        </div>
      ),
    },
  ]

  return (
    <SectionCard title="Supporting documents">
      <div className="flex flex-col" style={{ padding: '16px 20px', gap: 16 }}>
        <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px' }}>
          Optional. Annual reports, financial statements, press releases or anything else that supports your submission.
          Files are private to Stears and are scanned before anyone can open them.
        </p>
        <Checkbox checked={toggled} onChange={onToggle} label="No documents to upload" disabled={readOnly} />
        {!toggled && (
          <>
            <DropZone onFiles={upload} accept={UPLOAD_ACCEPT} maxSizeMb={25} disabled={readOnly || busy} />
            <div role="status" aria-live="polite">
              {results.length > 0 && (
                <ul className="flex flex-col font-mulish" style={{ gap: 4, margin: 0, padding: 0, listStyle: 'none' }}>
                  {results.map((r, i) => (
                    <li key={`${i}-${r.fileName}`} style={{ fontSize: 12, lineHeight: '16px', color: r.ok ? colors.success : colors.buttonDestructive }}>
                      <bdi>{r.fileName}</bdi>: {r.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {error && <span role="alert" className="font-mulish" style={{ fontSize: 10, color: colors.buttonDestructive }}>{error}</span>}
            <DataTable columns={columns} rows={items} rowKey={(a) => a.id} caption="Attached files" emptyMessage="No files attached yet" />
          </>
        )}
      </div>
    </SectionCard>
  )
}
